/**
 * JobMonitor.js
 *
 * Superuser-only view of every scheduled chatbot job (auto campaigns, EOD sync,
 * reminders) across tenants: health, scheduler heartbeat, run history, campaign runs,
 * send outcomes and errors — plus controls to release a stuck job, run now,
 * pause/resume, and close interrupted campaign runs. Every action is audited server-side.
 * Talks to GET /api/chatbot/admin/jobs, GET /admin/jobs/{id}, POST /admin/jobs/{id}/action.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Activity, RefreshCw, Loader2, AlertTriangle, ChevronDown, ChevronRight, Unlock,
  Play, Pause, PlayCircle, XCircle, Search, X,
} from 'lucide-react';
import { apiCall, apiCallJson } from './Api';
import { PageLayout, PageHeader, PageBody } from './PageLayout';

const HEALTH = {
  stuck:       { label: 'Stuck',       cls: 'bg-red-100 text-red-700 border-red-200',          hint: 'Marked running for longer than the stale limit — the run died or hung.' },
  overdue:     { label: 'Overdue',     cls: 'bg-orange-100 text-orange-700 border-orange-200', hint: 'Due but not picked up — check the scheduler heartbeat.' },
  failed:      { label: 'Failed',      cls: 'bg-red-50 text-red-600 border-red-200',           hint: 'Last run failed; it will retry on its interval.' },
  unscheduled: { label: 'Unscheduled', cls: 'bg-yellow-100 text-yellow-800 border-yellow-200', hint: 'Active but has no next run time.' },
  running:     { label: 'Running',     cls: 'bg-blue-100 text-blue-700 border-blue-200',       hint: 'A run is in progress.' },
  paused:      { label: 'Paused',      cls: 'bg-gray-200 text-gray-700 border-gray-300',       hint: 'Paused by a superadmin.' },
  disabled:    { label: 'Disabled',    cls: 'bg-gray-100 text-gray-500 border-gray-200',       hint: 'Turned off in the workflow, or its workflow is gone.' },
  ok:          { label: 'OK',          cls: 'bg-green-100 text-green-700 border-green-200',    hint: 'Scheduled normally.' },
};
const HEALTH_ORDER = ['stuck', 'overdue', 'failed', 'unscheduled', 'running', 'paused', 'disabled', 'ok'];
const TYPE_LABEL = { campaign_auto: 'Auto campaign', eod_sync: 'EOD sync', reminder: 'Reminder' };

const rel = (iso, now) => {
  if (!iso) return '—';
  const s = Math.round((new Date(iso).getTime() - now) / 1000);
  const a = Math.abs(s);
  const t = a < 60 ? `${a}s` : a < 3600 ? `${Math.round(a / 60)}m` : a < 86400 ? `${Math.round(a / 3600)}h` : `${Math.round(a / 86400)}d`;
  return s >= 0 ? `in ${t}` : `${t} ago`;
};
const abs = (iso) => (iso ? new Date(iso).toLocaleString() : '');
const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));

const Badge = ({ health }) => {
  const h = HEALTH[health] || HEALTH.ok;
  return <span title={h.hint} className={`inline-block px-2 py-0.5 text-xs font-medium rounded border ${h.cls}`}>{h.label}</span>;
};

// ── Action dialog ─────────────────────────────────────────────────────────────
const ACTION_INFO = {
  release:    { title: 'Release stuck job', btn: 'Release', danger: true },
  run_now:    { title: 'Run now', btn: 'Run now', danger: true },
  pause:      { title: 'Pause job', btn: 'Pause' },
  resume:     { title: 'Resume job', btn: 'Resume', danger: true },
  close_runs: { title: 'Close running campaign runs', btn: 'Close runs' },
};

const ActionDialog = ({ job, action, onClose, onDone }) => {
  const info = ACTION_INFO[action];
  const interval = job.config?.interval_minutes;
  const sends = job.job_type === 'campaign_auto' || job.job_type === 'reminder';
  const [delay, setDelay] = useState(action === 'release' ? 10 : (interval || 60));
  const [closeRuns, setCloseRuns] = useState(true);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const body = { action, reason };
      if (action === 'release' || action === 'resume') body.delay_minutes = Number(delay);
      if (action === 'release' || action === 'run_now') body.close_runs = closeRuns;
      const resp = await apiCall(`/api/chatbot/admin/jobs/${job.id}/action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(d.detail || `HTTP ${resp.status}`);
      onDone(d.message || 'Done');
    } catch (e) {
      setErr(e.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">{info.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <div className="text-gray-600">
            <span className="font-mono text-xs">{job.job_node_id}</span> · {job.workflow_name || job.workflow_id} · {job.host_tenant_id}
          </div>
          {(action === 'release' || action === 'resume') && (
            <label className="block">
              <span className="text-gray-700">Next run in (minutes)</span>
              <input type="number" min="0" value={delay} onChange={(e) => setDelay(e.target.value)}
                className="mt-1 w-full border rounded px-2 py-1.5" />
            </label>
          )}
          {(action === 'release' || (action === 'run_now' && job.health === 'stuck')) && job.config?.campaign_node_id && (
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={closeRuns} onChange={(e) => setCloseRuns(e.target.checked)} className="mt-0.5" />
              <span className="text-gray-700">Also close its running campaign run (recommended — the next run starts from row 0 and skips rows already sent)</span>
            </label>
          )}
          {action === 'close_runs' && (
            <p className="text-gray-600">Marks the campaign's running run(s) as interrupted. Nothing is sent. The next run re-reads from row 0 and skips rows already sent.</p>
          )}
          {action === 'pause' && (
            <p className="text-gray-600">Stops future runs. A run already in progress finishes. Saving the workflow does not un-pause it.</p>
          )}
          {sends && action !== 'pause' && action !== 'close_runs' && (
            <div className="flex gap-2 p-2.5 rounded bg-amber-50 border border-amber-200 text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>When it runs, every eligible contact not yet messaged gets a message — including rows added while the job was blocked. Check the sheet first.</span>
            </div>
          )}
          <label className="block">
            <span className="text-gray-700">Reason (recorded in history)</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
              className="mt-1 w-full border rounded px-2 py-1.5" placeholder="optional" />
          </label>
          {err && <div className="text-red-600">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border">Cancel</button>
          <button onClick={submit} disabled={busy}
            className={`px-3 py-1.5 text-sm rounded text-white flex items-center gap-1.5 disabled:opacity-60 ${info.danger ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{info.btn}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Detail panel ─────────────────────────────────────────────────────────────
const Section = ({ title, children }) => (
  <div>
    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">{title}</h4>
    {children}
  </div>
);
const Empty = ({ children }) => <div className="text-xs text-gray-400">{children}</div>;

const JobDetail = ({ jobId, now, refreshKey }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    apiCallJson(`/api/chatbot/admin/jobs/${jobId}`)
      .then((x) => live && setD(x))
      .catch((e) => live && setErr((e && e.message) || 'Failed to load job detail.'));
    return () => { live = false; };
  }, [jobId, refreshKey]);
  if (err) return <div className="p-4 text-sm text-red-600">{err}</div>;
  if (!d) return <div className="p-4 text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;

  return (
    <div className="p-4 bg-gray-50 grid gap-5 lg:grid-cols-2 text-sm">
      <Section title="Run history (latest 50)">
        {d.events.length === 0 ? <Empty>No history yet (recorded from this release onward).</Empty> : (
          <div className="max-h-72 overflow-y-auto border rounded bg-white divide-y">
            {d.events.map((e) => (
              <div key={e.id} className="px-2.5 py-1.5">
                <div className="flex items-center gap-2 text-xs">
                  <span className={`font-medium ${e.status === 'failed' ? 'text-red-600' : e.kind === 'admin' ? 'text-purple-700' : 'text-gray-800'}`}>
                    {e.kind === 'admin' ? `${e.actor}: ${e.status}` : e.status}
                  </span>
                  <span className="text-gray-400" title={abs(e.at)}>{rel(e.at, now)}</span>
                  {e.duration_ms != null && <span className="text-gray-400">· {(e.duration_ms / 1000).toFixed(1)}s</span>}
                </div>
                {e.detail && <div className="text-xs text-gray-600 break-words">{e.detail}</div>}
              </div>
            ))}
          </div>
        )}
      </Section>

      <div className="space-y-4">
        <Section title="Current state">
          <div className="text-xs text-gray-700 space-y-0.5">
            <div>Last status: <span className="font-mono break-all">{d.job.last_run_status || '—'}</span></div>
            <div>Last run: {abs(d.job.last_run_at) || '—'} · Next run: {abs(d.job.next_run_at) || '—'}</div>
            <div>Config: <span className="font-mono break-all">{JSON.stringify(d.job.config)}</span></div>
            {d.pending_writeback != null && <div>Contacts waiting for write-back: <b>{n(d.pending_writeback)}</b></div>}
          </div>
        </Section>

        {d.runs.length > 0 && (
          <Section title="Campaign runs">
            <table className="w-full text-xs bg-white border rounded">
              <thead className="bg-gray-100 text-gray-600"><tr>
                <th className="text-left px-2 py-1">Started</th><th className="text-left px-2 py-1">Status</th>
                <th className="text-right px-2 py-1">Sent</th><th className="text-right px-2 py-1">Skipped</th>
                <th className="text-right px-2 py-1">Failed</th><th className="text-right px-2 py-1">Last row</th>
              </tr></thead>
              <tbody className="divide-y">
                {d.runs.map((r) => (
                  <tr key={r.id} className={r.status === 'running' ? 'bg-blue-50' : ''}>
                    <td className="px-2 py-1" title={abs(r.started_at)}>{rel(r.started_at, now)}</td>
                    <td className="px-2 py-1">{r.status}{r.status === 'running' && <span className="text-gray-400"> · {r.channel}</span>}</td>
                    <td className="px-2 py-1 text-right">{n(r.sent_count)}</td>
                    <td className="px-2 py-1 text-right">{n(r.skipped_count)}</td>
                    <td className="px-2 py-1 text-right">{n(r.failed_count)}</td>
                    <td className="px-2 py-1 text-right">{n(r.last_row_index)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {(d.sends.length > 0 || d.replies.length > 0) && (
          <Section title="Contacts (all time)">
            <div className="flex flex-wrap gap-1.5">
              {d.sends.map((s) => (
                <span key={`s-${s.send_status}`} className={`text-xs px-2 py-0.5 rounded border bg-white ${s.send_status === 'failed' ? 'text-red-600' : ''}`}>
                  {s.send_status}: <b>{n(s.n)}</b>
                </span>
              ))}
              {d.replies.map((r) => (
                <span key={`r-${r.response_status}`} className="text-xs px-2 py-0.5 rounded border bg-white text-gray-600">
                  {r.response_status || 'no status'}: <b>{n(r.n)}</b>
                </span>
              ))}
            </div>
          </Section>
        )}

        {d.errors.length > 0 && (
          <Section title="Top send errors">
            <div className="border rounded bg-white divide-y">
              {d.errors.map((e, i) => (
                <div key={i} className="px-2.5 py-1 text-xs flex gap-2">
                  <b className="text-red-600 shrink-0">{n(e.n)}×</b>
                  <span className="break-words text-gray-700">{e.error}</span>
                  <span className="ml-auto text-gray-400 shrink-0">{rel(e.last_at, now)}</span>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const JobMonitor = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [auto, setAuto] = useState(true);
  const [filter, setFilter] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const [dialog, setDialog] = useState(null);   // { job, action }
  const [toast, setToast] = useState('');
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const d = await apiCallJson('/api/chatbot/admin/jobs');
      setData(d); setError('');
    } catch (e) {
      setError((e && e.message) || 'Failed to load jobs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(() => { load(); setTick((x) => x + 1); }, 30000);
    return () => clearInterval(t);
  }, [auto, load]);

  const now = data ? new Date(data.server_time).getTime() : Date.now();
  const jobs = data?.jobs || [];
  const counts = useMemo(() => jobs.reduce((a, j) => ({ ...a, [j.health]: (a[j.health] || 0) + 1 }), {}), [jobs]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return jobs
      .filter((j) => !filter || j.health === filter)
      .filter((j) => !s || [j.host_tenant_id, j.workflow_name, j.workflow_id, j.job_node_id, j.last_run_status]
        .some((v) => (v || '').toLowerCase().includes(s)))
      .sort((a, b) => HEALTH_ORDER.indexOf(a.health) - HEALTH_ORDER.indexOf(b.health));
  }, [jobs, filter, q]);
  const hbs = data?.heartbeats || [];

  const done = (msg) => { setDialog(null); setToast(msg); load(); setTick((x) => x + 1); setTimeout(() => setToast(''), 6000); };

  const actionsFor = (j) => {
    const a = [];
    if (j.health === 'stuck') a.push(['release', 'Release', Unlock]);
    if (j.is_active && j.health !== 'running') a.push(['run_now', 'Run now', Play]);
    if (j.is_active) a.push(['pause', 'Pause', Pause]);
    else a.push(['resume', 'Resume', PlayCircle]);
    if (j.run_status === 'running' && j.health !== 'running' && j.health !== 'stuck') a.push(['close_runs', 'Close run', XCircle]);
    return a;
  };

  return (
    <PageLayout>
      <PageHeader title="Job Monitor" icon={Activity} actions={<>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> Auto-refresh 30s
        </label>
        <button onClick={() => { setLoading(true); load(); }} className="px-3 py-1.5 text-sm rounded border flex items-center gap-1.5 hover:bg-gray-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </>} />
      <PageBody>
        {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
        {toast && <div className="mb-4 p-3 rounded bg-green-50 border border-green-200 text-sm text-green-800">{toast}</div>}

        {data && (
          <div className={`mb-4 p-3 rounded border text-sm ${hbs.length ? 'bg-white' : 'bg-red-50 border-red-200'}`}>
            {hbs.length === 0 ? (
              <div className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-4 h-4" />
                No scheduler heartbeat in the last 5 minutes — the scheduler is not running on any worker (or Redis is unreachable). Due jobs will not run.
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="font-medium text-gray-700">Scheduler workers:</span>
                {hbs.map((h) => (
                  <span key={`${h.host}:${h.pid}`} className={`text-xs ${h.age_seconds > 150 ? 'text-orange-600' : 'text-gray-600'}`}>
                    <span className={`inline-block w-2 h-2 rounded-full mr-1 ${h.age_seconds > 150 ? 'bg-orange-500' : 'bg-green-500'}`} />
                    {h.host}:{h.pid} · poll #{h.poll} · {h.age_seconds}s ago
                  </span>
                ))}
                <span className="text-xs text-gray-400">Stale-claim limit: {data.stale_running_minutes} min</span>
              </div>
            )}
          </div>
        )}

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button onClick={() => setFilter('')}
            className={`text-xs px-2.5 py-1 rounded border ${!filter ? 'bg-gray-800 text-white border-gray-800' : 'bg-white'}`}>
            All {jobs.length}
          </button>
          {HEALTH_ORDER.filter((h) => counts[h]).map((h) => (
            <button key={h} onClick={() => setFilter(filter === h ? '' : h)}
              className={`text-xs px-2.5 py-1 rounded border ${filter === h ? 'ring-2 ring-offset-1 ring-gray-400' : ''} ${HEALTH[h].cls}`}>
              {HEALTH[h].label} {counts[h]}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tenant, workflow, node, status…"
              className="pl-7 pr-2 py-1.5 text-sm border rounded w-64" />
          </div>
        </div>

        {loading && !data ? (
          <div className="p-6 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="bg-white border rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="w-6" />
                  <th className="text-left px-3 py-2">Health</th>
                  <th className="text-left px-3 py-2">Tenant / workflow</th>
                  <th className="text-left px-3 py-2">Job</th>
                  <th className="text-left px-3 py-2">Last run</th>
                  <th className="text-left px-3 py-2">Next run</th>
                  <th className="text-left px-3 py-2">Latest campaign run</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {shown.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">No jobs match.</td></tr>
                )}
                {shown.map((j) => (
                  <React.Fragment key={j.id}>
                    <tr className={`hover:bg-gray-50 ${open === j.id ? 'bg-gray-50' : ''}`}>
                      <td className="pl-2 cursor-pointer" onClick={() => setOpen(open === j.id ? null : j.id)}>
                        {open === j.id ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                      </td>
                      <td className="px-3 py-2"><Badge health={j.health} /></td>
                      <td className="px-3 py-2">
                        <div className="text-gray-900">{j.workflow_name || <span className="text-gray-400">(workflow missing)</span>}</div>
                        <div className="text-xs text-gray-500">{j.host_tenant_id} · <span className="font-mono">{j.workflow_id}</span></div>
                      </td>
                      <td className="px-3 py-2">
                        <div>{TYPE_LABEL[j.job_type] || j.job_type}</div>
                        <div className="text-xs text-gray-500 font-mono">{j.job_node_id}{j.config?.interval_minutes ? ` · every ${j.config.interval_minutes}m` : ''}</div>
                      </td>
                      <td className="px-3 py-2 max-w-xs">
                        <div title={abs(j.last_run_at)}>{rel(j.last_run_at, now)}</div>
                        <div className={`text-xs truncate ${(j.last_run_status || '').startsWith('failed') ? 'text-red-600' : 'text-gray-500'}`}
                          title={j.last_run_status}>{j.last_run_status || '—'}</div>
                      </td>
                      <td className="px-3 py-2" title={abs(j.next_run_at)}>{j.is_active ? rel(j.next_run_at, now) : '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {j.run_id ? (
                          <>
                            <div className={j.run_status === 'running' ? 'text-blue-700 font-medium' : ''}>{j.run_status} · {rel(j.run_started_at, now)}</div>
                            <div>{n(j.run_sent)} sent · {n(j.run_skipped)} skipped · {n(j.run_failed)} failed</div>
                          </>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          {actionsFor(j).map(([act, label, Icon]) => (
                            <button key={act} onClick={() => setDialog({ job: j, action: act })} title={label}
                              className={`px-2 py-1 text-xs rounded border flex items-center gap-1 hover:bg-gray-100 ${act === 'release' ? 'border-red-300 text-red-700' : ''}`}>
                              <Icon className="w-3.5 h-3.5" />{label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                    {open === j.id && (
                      <tr><td colSpan={8} className="p-0 border-t"><JobDetail jobId={j.id} now={now} refreshKey={tick} /></td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageBody>
      {dialog && <ActionDialog job={dialog.job} action={dialog.action} onClose={() => setDialog(null)} onDone={done} />}
    </PageLayout>
  );
};

export default JobMonitor;
